import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Visibility as VisibilityIcon,
  Mouse as MouseIcon,
  AttachMoney as MoneyIcon,
  NotificationsActive as AlertIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import GoogleMCPNavigation from '../components/GoogleMCPNavigation';

interface SystemHealth {
  system_status: string;
  services: {
    google_ads: {
      status: string;
      configured: boolean;
    };
    google_analytics: {
      status: string;
      configured: boolean;
    };
  };
  timestamp: string;
}

interface DashboardMetrics {
  campaigns: {
    total: number;
    active: number;
    paused: number;
  };
  leads: {
    total_today: number;
    total_week: number;
    total_month: number;
    conversion_rate: number;
  };
  performance: {
    impressions: number;
    clicks: number;
    ctr: number;
    cost: number;
    cpc: number;
  };
  alerts: {
    total: number;
    unread: number;
    high_priority: number;
  };
}

const GoogleMCPDashboard: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkSystemHealth();
    loadDashboardMetrics();
  }, []);

  const checkSystemHealth = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google-mcp/health`);
      setHealth(response.data.data);
    } catch (err: any) {
      console.error('Erro ao verificar saúde do sistema:', err);
      setError('Erro ao conectar com o sistema MCP Google');
    }
  };

  const loadDashboardMetrics = async () => {
    setLoading(true);
    try {
      // Simular métricas por enquanto - depois integrar com APIs reais
      const mockMetrics: DashboardMetrics = {
        campaigns: {
          total: 5,
          active: 3,
          paused: 2
        },
        leads: {
          total_today: 12,
          total_week: 89,
          total_month: 342,
          conversion_rate: 3.2
        },
        performance: {
          impressions: 15420,
          clicks: 487,
          ctr: 3.16,
          cost: 1250.75,
          cpc: 2.57
        },
        alerts: {
          total: 8,
          unread: 3,
          high_priority: 1
        }
      };
      
      setMetrics(mockMetrics);
    } catch (err: any) {
      console.error('Erro ao carregar métricas:', err);
      setError('Erro ao carregar métricas do dashboard');
    }
    setLoading(false);
  };

  const initializeSystem = async () => {
    setInitializing(true);
    try {
      await axios.post(`${API_URL}/api/google-mcp/initialize`);
      await checkSystemHealth();
      setError(null);
    } catch (err: any) {
      console.error('Erro ao inicializar sistema:', err);
      setError('Erro ao inicializar sistema MCP Google');
    }
    setInitializing(false);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'healthy':
      case 'active':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'healthy':
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
      case 'inactive':
        return <ErrorIcon color="error" />;
      default:
        return <CircularProgress size={20} />;
    }
  };

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    color: string;
    onClick?: () => void;
  }> = ({ title, value, subtitle, icon, color, onClick }) => (
    <Card 
      sx={{ 
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              backgroundColor: `${color}20`,
              borderRadius: '50%',
              p: 1.5,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" fontWeight="medium">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <Typography variant="h4" component="div" fontWeight="bold" color={color}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading && !metrics) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Navegação */}
      <GoogleMCPNavigation currentPage="dashboard" />
      
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Dashboard MCP Google
          </Typography>
          <Box>
            <Tooltip title="Atualizar dados">
              <IconButton onClick={() => { checkSystemHealth(); loadDashboardMetrics(); }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/admin/google-mcp/settings')}
              sx={{ ml: 1 }}
            >
              Configurações
            </Button>
          </Box>
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          Monitoramento e gestão do sistema de captação de leads via Google Ads e Analytics
        </Typography>
      </Box>

      {/* Status do Sistema */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Status do Sistema
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button 
              size="small" 
              onClick={initializeSystem}
              disabled={initializing}
              sx={{ ml: 2 }}
            >
              {initializing ? 'Inicializando...' : 'Inicializar Sistema'}
            </Button>
          </Alert>
        )}

        {health && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getStatusIcon(health.system_status)}
                <Box sx={{ ml: 2 }}>
                  <Typography variant="body1" fontWeight="medium">
                    Sistema Geral
                  </Typography>
                  <Chip 
                    label={health.system_status} 
                    color={getStatusColor(health.system_status) as any}
                    size="small"
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getStatusIcon(health.services.google_ads.status)}
                <Box sx={{ ml: 2 }}>
                  <Typography variant="body1" fontWeight="medium">
                    Google Ads
                  </Typography>
                  <Chip 
                    label={health.services.google_ads.configured ? 'Configurado' : 'Não Configurado'}
                    color={health.services.google_ads.configured ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getStatusIcon(health.services.google_analytics.status)}
                <Box sx={{ ml: 2 }}>
                  <Typography variant="body1" fontWeight="medium">
                    Google Analytics
                  </Typography>
                  <Chip 
                    label={health.services.google_analytics.configured ? 'Configurado' : 'Não Configurado'}
                    color={health.services.google_analytics.configured ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Métricas Principais */}
      {metrics && (
        <>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Métricas de Performance
          </Typography>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Leads Hoje"
                value={metrics.leads.total_today}
                subtitle="Novos leads capturados"
                icon={<PeopleIcon sx={{ color: '#1976d2' }} />}
                color="#1976d2"
                onClick={() => navigate('/admin/google-mcp/leads')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Impressões"
                value={metrics.performance.impressions.toLocaleString()}
                subtitle="Últimos 7 dias"
                icon={<VisibilityIcon sx={{ color: '#2e7d32' }} />}
                color="#2e7d32"
                onClick={() => navigate('/admin/google-mcp/analytics')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="CTR"
                value={`${metrics.performance.ctr}%`}
                subtitle="Taxa de cliques"
                icon={<MouseIcon sx={{ color: '#ed6c02' }} />}
                color="#ed6c02"
                onClick={() => navigate('/admin/google-mcp/analytics')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Custo"
                value={`R$ ${metrics.performance.cost.toFixed(2)}`}
                subtitle="Últimos 7 dias"
                icon={<MoneyIcon sx={{ color: '#9c27b0' }} />}
                color="#9c27b0"
                onClick={() => navigate('/admin/google-mcp/analytics')}
              />
            </Grid>
          </Grid>

          {/* Ações Rápidas */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Ações Rápidas
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<CampaignIcon />}
                      onClick={() => navigate('/admin/google-mcp/campaigns')}
                      sx={{ py: 2 }}
                    >
                      Campanhas
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<PeopleIcon />}
                      onClick={() => navigate('/admin/google-mcp/leads')}
                      sx={{ py: 2 }}
                    >
                      Leads
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AnalyticsIcon />}
                      onClick={() => navigate('/admin/google-mcp/analytics')}
                      sx={{ py: 2 }}
                    >
                      Analytics
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AlertIcon />}
                      onClick={() => navigate('/admin/google-mcp/alerts')}
                      sx={{ py: 2 }}
                    >
                      Alertas
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Alertas Recentes
                </Typography>
                {metrics.alerts.total > 0 ? (
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <WarningIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="CTR baixo detectado"
                        secondary="Campanha 'Consórcio Carros'"
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemIcon>
                        <TrendingUpIcon color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Aumento de conversões"
                        secondary="+25% nas últimas 24h"
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemIcon>
                        <ErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Orçamento esgotado"
                        secondary="Campanha pausada"
                      />
                    </ListItem>
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum alerta no momento
                  </Typography>
                )}
                
                <Button 
                  fullWidth 
                  variant="text" 
                  onClick={() => navigate('/admin/google-mcp/alerts')}
                  sx={{ mt: 2 }}
                >
                  Ver Todos os Alertas
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default GoogleMCPDashboard;
